import { cn } from "@/lib/utils";

interface ChineseZodiacIconProps {
  animal: string;
  className?: string;
  size?: number;
}

const ChineseZodiacIcon = ({ animal, className, size = 64 }: ChineseZodiacIconProps) => {
  const iconSize = size;
  
  const icons: Record<string, JSX.Element> = {
    rata: (
      <svg viewBox="0 0 100 100" className={cn("", className)} width={iconSize} height={iconSize}>
        <defs>
          <linearGradient id="rata-gradient" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#A78BFA" />
            <stop offset="50%" stopColor="#8B5CF6" />
            <stop offset="100%" stopColor="#7C3AED" />
          </linearGradient>
          <filter id="rata-glow">
            <feGaussianBlur stdDeviation="2" result="coloredBlur"/>
            <feMerge>
              <feMergeNode in="coloredBlur"/>
              <feMergeNode in="SourceGraphic"/>
            </feMerge>
          </filter>
        </defs>
        <circle cx="50" cy="50" r="46" fill="url(#rata-gradient)" opacity="0.15" />
        <circle cx="50" cy="50" r="42" fill="none" stroke="url(#rata-gradient)" strokeWidth="1" opacity="0.3" />
        <g fill="none" stroke="url(#rata-gradient)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" filter="url(#rata-glow)">
          {/* Orejas grandes y redondeadas */}
          <ellipse cx="32" cy="28" rx="14" ry="16" fill="url(#rata-gradient)" opacity="0.2" />
          <ellipse cx="68" cy="28" rx="14" ry="16" fill="url(#rata-gradient)" opacity="0.2" />
          <ellipse cx="32" cy="28" rx="14" ry="16" />
          <ellipse cx="68" cy="28" rx="14" ry="16" />
          <ellipse cx="32" cy="28" rx="8" ry="10" fill="url(#rata-gradient)" opacity="0.3" />
          <ellipse cx="68" cy="28" rx="8" ry="10" fill="url(#rata-gradient)" opacity="0.3" />
          {/* Cabeza */}
          <ellipse cx="50" cy="55" rx="26" ry="24" fill="url(#rata-gradient)" opacity="0.1" />
          <ellipse cx="50" cy="55" rx="26" ry="24" />
          {/* Ojos brillantes */}
          <circle cx="40" cy="50" r="5" fill="url(#rata-gradient)" />
          <circle cx="60" cy="50" r="5" fill="url(#rata-gradient)" />
          <circle cx="42" cy="48" r="2" fill="white" opacity="0.9" />
          <circle cx="62" cy="48" r="2" fill="white" opacity="0.9" />
          {/* Nariz */}
          <ellipse cx="50" cy="62" rx="5" ry="4" fill="url(#rata-gradient)" />
          <ellipse cx="50" cy="61" rx="2" ry="1.5" fill="white" opacity="0.4" />
          {/* Bigotes elegantes */}
          <path d="M32 58 L14 52" strokeWidth="1.5" />
          <path d="M32 62 L14 62" strokeWidth="1.5" />
          <path d="M32 66 L14 72" strokeWidth="1.5" />
          <path d="M68 58 L86 52" strokeWidth="1.5" />
          <path d="M68 62 L86 62" strokeWidth="1.5" />
          <path d="M68 66 L86 72" strokeWidth="1.5" />
          {/* Boca */}
          <path d="M45 68 Q50 72 55 68" strokeWidth="1.5" />
          {/* Cola elegante */}
          <path d="M76 70 Q92 72 88 88 Q85 92 80 88" strokeWidth="2" />
        </g>
      </svg>
    ),
    buey: (
      <svg viewBox="0 0 100 100" className={cn("", className)} width={iconSize} height={iconSize}>
        <defs>
          <linearGradient id="buey-gradient" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#F59E0B" />
            <stop offset="50%" stopColor="#D97706" />
            <stop offset="100%" stopColor="#B45309" />
          </linearGradient>
          <filter id="buey-glow">
            <feGaussianBlur stdDeviation="2" result="coloredBlur"/>
            <feMerge>
              <feMergeNode in="coloredBlur"/>
              <feMergeNode in="SourceGraphic"/>
            </feMerge>
          </filter>
        </defs>
        <circle cx="50" cy="50" r="46" fill="url(#buey-gradient)" opacity="0.15" />
        <circle cx="50" cy="50" r="42" fill="none" stroke="url(#buey-gradient)" strokeWidth="1" opacity="0.3" />
        <g fill="none" stroke="url(#buey-gradient)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" filter="url(#buey-glow)">
          {/* Cuernos majestuosos */}
          <path d="M18 38 Q8 22 20 12 Q26 8 30 14" strokeWidth="3" fill="url(#buey-gradient)" opacity="0.3" />
          <path d="M82 38 Q92 22 80 12 Q74 8 70 14" strokeWidth="3" fill="url(#buey-gradient)" opacity="0.3" />
          <path d="M18 38 Q8 22 20 12 Q26 8 30 14" strokeWidth="2.5" />
          <path d="M82 38 Q92 22 80 12 Q74 8 70 14" strokeWidth="2.5" />
          {/* Orejas */}
          <path d="M22 42 Q12 38 16 30" strokeWidth="2" />
          <path d="M78 42 Q88 38 84 30" strokeWidth="2" />
          {/* Cabeza */}
          <ellipse cx="50" cy="52" rx="32" ry="28" fill="url(#buey-gradient)" opacity="0.1" />
          <ellipse cx="50" cy="52" rx="32" ry="28" />
          {/* Ojos */}
          <circle cx="36" cy="46" r="5" fill="url(#buey-gradient)" />
          <circle cx="64" cy="46" r="5" fill="url(#buey-gradient)" />
          <circle cx="38" cy="44" r="2" fill="white" opacity="0.9" />
          <circle cx="66" cy="44" r="2" fill="white" opacity="0.9" />
          {/* Hocico grande */}
          <ellipse cx="50" cy="66" rx="16" ry="10" fill="url(#buey-gradient)" opacity="0.2" />
          <ellipse cx="50" cy="66" rx="16" ry="10" />
          {/* Fosas nasales */}
          <ellipse cx="44" cy="66" rx="3" ry="4" fill="url(#buey-gradient)" />
          <ellipse cx="56" cy="66" rx="3" ry="4" fill="url(#buey-gradient)" />
          {/* Aro nasal */}
          <path d="M42 72 Q50 78 58 72" strokeWidth="2" />
        </g>
      </svg>
    ),
    tigre: (
      <svg viewBox="0 0 100 100" className={cn("", className)} width={iconSize} height={iconSize}>
        <defs>
          <linearGradient id="tigre-gradient" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#FB923C" />
            <stop offset="50%" stopColor="#F97316" />
            <stop offset="100%" stopColor="#EA580C" />
          </linearGradient>
          <filter id="tigre-glow">
            <feGaussianBlur stdDeviation="2" result="coloredBlur"/>
            <feMerge>
              <feMergeNode in="coloredBlur"/>
              <feMergeNode in="SourceGraphic"/>
            </feMerge>
          </filter>
        </defs>
        <circle cx="50" cy="50" r="46" fill="url(#tigre-gradient)" opacity="0.15" />
        <circle cx="50" cy="50" r="42" fill="none" stroke="url(#tigre-gradient)" strokeWidth="1" opacity="0.3" />
        <g fill="none" stroke="url(#tigre-gradient)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" filter="url(#tigre-glow)">
          {/* Orejas con pelaje */}
          <path d="M22 18 L18 38 L36 32 Z" fill="url(#tigre-gradient)" opacity="0.3" />
          <path d="M78 18 L82 38 L64 32 Z" fill="url(#tigre-gradient)" opacity="0.3" />
          <path d="M22 18 L18 38 L36 32 Z" />
          <path d="M78 18 L82 38 L64 32 Z" />
          {/* Cabeza */}
          <ellipse cx="50" cy="52" rx="34" ry="30" fill="url(#tigre-gradient)" opacity="0.1" />
          <ellipse cx="50" cy="52" rx="34" ry="30" />
          {/* Rayas distintivas */}
          <path d="M50 24 L50 36" strokeWidth="3" />
          <path d="M40 26 L42 38" strokeWidth="2.5" />
          <path d="M60 26 L58 38" strokeWidth="2.5" />
          <path d="M32 34 L38 42" strokeWidth="2" />
          <path d="M68 34 L62 42" strokeWidth="2" />
          {/* Ojos felinos */}
          <ellipse cx="36" cy="48" rx="7" ry="5" fill="url(#tigre-gradient)" />
          <ellipse cx="64" cy="48" rx="7" ry="5" fill="url(#tigre-gradient)" />
          <ellipse cx="36" cy="48" rx="2" ry="4" fill="#000" opacity="0.6" />
          <ellipse cx="64" cy="48" rx="2" ry="4" fill="#000" opacity="0.6" />
          <circle cx="38" cy="46" r="1.5" fill="white" opacity="0.9" />
          <circle cx="66" cy="46" r="1.5" fill="white" opacity="0.9" />
          {/* Nariz */}
          <path d="M44 60 L50 68 L56 60 Z" fill="url(#tigre-gradient)" />
          {/* Boca y colmillos */}
          <path d="M50 68 L50 74" strokeWidth="1.5" />
          <path d="M50 74 Q40 80 36 74" strokeWidth="1.5" />
          <path d="M50 74 Q60 80 64 74" strokeWidth="1.5" />
          {/* Bigotes */}
          <path d="M28 58 L12 52" strokeWidth="1.5" />
          <path d="M28 64 L12 66" strokeWidth="1.5" />
          <path d="M72 58 L88 52" strokeWidth="1.5" />
          <path d="M72 64 L88 66" strokeWidth="1.5" />
        </g>
      </svg>
    ),
    conejo: (
      <svg viewBox="0 0 100 100" className={cn("", className)} width={iconSize} height={iconSize}>
        <defs>
          <linearGradient id="conejo-gradient" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#F9A8D4" />
            <stop offset="50%" stopColor="#EC4899" />
            <stop offset="100%" stopColor="#DB2777" />
          </linearGradient>
          <filter id="conejo-glow">
            <feGaussianBlur stdDeviation="2" result="coloredBlur"/>
            <feMerge>
              <feMergeNode in="coloredBlur"/>
              <feMergeNode in="SourceGraphic"/>
            </feMerge>
          </filter>
        </defs>
        <circle cx="50" cy="50" r="46" fill="url(#conejo-gradient)" opacity="0.15" />
        <circle cx="50" cy="50" r="42" fill="none" stroke="url(#conejo-gradient)" strokeWidth="1" opacity="0.3" />
        <g fill="none" stroke="url(#conejo-gradient)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" filter="url(#conejo-glow)">
          {/* Orejas largas y elegantes */}
          <ellipse cx="36" cy="22" rx="9" ry="22" fill="url(#conejo-gradient)" opacity="0.2" />
          <ellipse cx="64" cy="22" rx="9" ry="22" fill="url(#conejo-gradient)" opacity="0.2" />
          <ellipse cx="36" cy="22" rx="9" ry="22" />
          <ellipse cx="64" cy="22" rx="9" ry="22" />
          <ellipse cx="36" cy="20" rx="4" ry="14" fill="url(#conejo-gradient)" opacity="0.4" />
          <ellipse cx="64" cy="20" rx="4" ry="14" fill="url(#conejo-gradient)" opacity="0.4" />
          {/* Cabeza suave */}
          <ellipse cx="50" cy="60" rx="28" ry="24" fill="url(#conejo-gradient)" opacity="0.1" />
          <ellipse cx="50" cy="60" rx="28" ry="24" />
          {/* Mejillas esponjosas */}
          <ellipse cx="30" cy="62" rx="8" ry="6" fill="url(#conejo-gradient)" opacity="0.15" />
          <ellipse cx="70" cy="62" rx="8" ry="6" fill="url(#conejo-gradient)" opacity="0.15" />
          {/* Ojos grandes y tiernos */}
          <circle cx="40" cy="55" r="7" fill="url(#conejo-gradient)" />
          <circle cx="60" cy="55" r="7" fill="url(#conejo-gradient)" />
          <circle cx="42" cy="53" r="3" fill="white" opacity="0.9" />
          <circle cx="62" cy="53" r="3" fill="white" opacity="0.9" />
          <circle cx="40" cy="57" r="1.5" fill="white" opacity="0.5" />
          <circle cx="60" cy="57" r="1.5" fill="white" opacity="0.5" />
          {/* Nariz corazón */}
          <path d="M48 68 Q50 65 52 68 L50 72 Z" fill="url(#conejo-gradient)" />
          {/* Boca */}
          <path d="M50 72 L50 75" strokeWidth="1.5" />
          <path d="M50 75 Q44 79 40 75" strokeWidth="1.5" />
          <path d="M50 75 Q56 79 60 75" strokeWidth="1.5" />
          {/* Bigotes delicados */}
          <path d="M34 68 L18 64" strokeWidth="1" />
          <path d="M34 72 L18 74" strokeWidth="1" />
          <path d="M66 68 L82 64" strokeWidth="1" />
          <path d="M66 72 L82 74" strokeWidth="1" />
        </g>
      </svg>
    ),
    dragon: (
      <svg viewBox="0 0 100 100" className={cn("", className)} width={iconSize} height={iconSize}>
        <defs>
          <linearGradient id="dragon-gradient" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#34D399" />
            <stop offset="50%" stopColor="#10B981" />
            <stop offset="100%" stopColor="#059669" />
          </linearGradient>
          <filter id="dragon-glow">
            <feGaussianBlur stdDeviation="2.5" result="coloredBlur"/>
            <feMerge>
              <feMergeNode in="coloredBlur"/>
              <feMergeNode in="SourceGraphic"/>
            </feMerge>
          </filter>
        </defs>
        <circle cx="50" cy="50" r="46" fill="url(#dragon-gradient)" opacity="0.15" />
        <circle cx="50" cy="50" r="42" fill="none" stroke="url(#dragon-gradient)" strokeWidth="1" opacity="0.3" />
        <g fill="none" stroke="url(#dragon-gradient)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" filter="url(#dragon-glow)">
          {/* Cuernos majestuosos */}
          <path d="M22 34 Q14 18 26 8 Q32 4 36 12" strokeWidth="2.5" fill="url(#dragon-gradient)" opacity="0.3" />
          <path d="M78 34 Q86 18 74 8 Q68 4 64 12" strokeWidth="2.5" fill="url(#dragon-gradient)" opacity="0.3" />
          <path d="M22 34 Q14 18 26 8 Q32 4 36 12" strokeWidth="2" />
          <path d="M78 34 Q86 18 74 8 Q68 4 64 12" strokeWidth="2" />
          {/* Cresta de espinas */}
          <path d="M38 16 L42 26" strokeWidth="2" />
          <path d="M50 12 L50 22" strokeWidth="2.5" />
          <path d="M62 16 L58 26" strokeWidth="2" />
          {/* Cabeza */}
          <ellipse cx="50" cy="50" rx="30" ry="28" fill="url(#dragon-gradient)" opacity="0.1" />
          <ellipse cx="50" cy="50" rx="30" ry="28" />
          {/* Escamas decorativas */}
          <path d="M26 44 Q30 40 34 44" strokeWidth="1.5" opacity="0.6" />
          <path d="M66 44 Q70 40 74 44" strokeWidth="1.5" opacity="0.6" />
          {/* Hocico */}
          <path d="M32 62 Q50 82 68 62" strokeWidth="2" />
          {/* Ojos místicos */}
          <ellipse cx="38" cy="44" rx="8" ry="6" fill="url(#dragon-gradient)" />
          <ellipse cx="62" cy="44" rx="8" ry="6" fill="url(#dragon-gradient)" />
          <ellipse cx="38" cy="44" rx="2" ry="5" fill="#000" opacity="0.5" />
          <ellipse cx="62" cy="44" rx="2" ry="5" fill="#000" opacity="0.5" />
          <circle cx="40" cy="42" r="2" fill="white" opacity="0.9" />
          <circle cx="64" cy="42" r="2" fill="white" opacity="0.9" />
          {/* Fosas nasales humeantes */}
          <circle cx="42" cy="64" r="3" fill="url(#dragon-gradient)" />
          <circle cx="58" cy="64" r="3" fill="url(#dragon-gradient)" />
          {/* Fuego */}
          <path d="M46 78 Q48 86 50 90" stroke="#FCD34D" strokeWidth="2" opacity="0.8" />
          <path d="M50 76 Q52 84 54 92" stroke="#F97316" strokeWidth="2" opacity="0.7" />
          <path d="M54 78 Q56 86 54 90" stroke="#EF4444" strokeWidth="1.5" opacity="0.6" />
        </g>
      </svg>
    ),
    serpiente: (
      <svg viewBox="0 0 100 100" className={cn("", className)} width={iconSize} height={iconSize}>
        <defs>
          <linearGradient id="serpiente-gradient" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#5EEAD4" />
            <stop offset="50%" stopColor="#14B8A6" />
            <stop offset="100%" stopColor="#0D9488" />
          </linearGradient>
          <filter id="serpiente-glow">
            <feGaussianBlur stdDeviation="2" result="coloredBlur"/>
            <feMerge>
              <feMergeNode in="coloredBlur"/>
              <feMergeNode in="SourceGraphic"/>
            </feMerge>
          </filter>
        </defs>
        <circle cx="50" cy="50" r="46" fill="url(#serpiente-gradient)" opacity="0.15" />
        <circle cx="50" cy="50" r="42" fill="none" stroke="url(#serpiente-gradient)" strokeWidth="1" opacity="0.3" />
        <g fill="none" stroke="url(#serpiente-gradient)" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" filter="url(#serpiente-glow)">
          {/* Cuerpo enroscado elegante */}
          <path d="M22 82 Q8 65 22 48 Q36 32 52 48 Q68 64 52 80 Q38 94 22 82" strokeWidth="4" fill="url(#serpiente-gradient)" opacity="0.15" />
          <path d="M22 82 Q8 65 22 48 Q36 32 52 48 Q68 64 52 80 Q38 94 22 82" strokeWidth="3" />
          {/* Patrón de escamas */}
          <path d="M28 54 Q34 50 40 54" strokeWidth="1.5" opacity="0.5" />
          <path d="M42 66 Q48 62 54 66" strokeWidth="1.5" opacity="0.5" />
          <path d="M32 76 Q38 72 44 76" strokeWidth="1.5" opacity="0.5" />
          {/* Cabeza elegante */}
          <ellipse cx="58" cy="34" rx="20" ry="16" fill="url(#serpiente-gradient)" opacity="0.2" />
          <ellipse cx="58" cy="34" rx="20" ry="16" />
          {/* Conexión cuello */}
          <path d="M45 46 Q38 54 42 64" strokeWidth="3" />
          {/* Ojos hipnóticos */}
          <ellipse cx="50" cy="30" rx="5" ry="4" fill="url(#serpiente-gradient)" />
          <ellipse cx="66" cy="30" rx="5" ry="4" fill="url(#serpiente-gradient)" />
          <ellipse cx="50" cy="30" rx="1.5" ry="3" fill="#000" opacity="0.5" />
          <ellipse cx="66" cy="30" rx="1.5" ry="3" fill="#000" opacity="0.5" />
          <circle cx="51" cy="28" r="1.5" fill="white" opacity="0.9" />
          <circle cx="67" cy="28" r="1.5" fill="white" opacity="0.9" />
          {/* Lengua bífida */}
          <path d="M76 38 L86 34" strokeWidth="2" />
          <path d="M86 34 L90 30" strokeWidth="1.5" />
          <path d="M86 34 L90 38" strokeWidth="1.5" />
        </g>
      </svg>
    ),
    caballo: (
      <svg viewBox="0 0 100 100" className={cn("", className)} width={iconSize} height={iconSize}>
        <defs>
          <linearGradient id="caballo-gradient" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#FB7185" />
            <stop offset="50%" stopColor="#EF4444" />
            <stop offset="100%" stopColor="#DC2626" />
          </linearGradient>
          <filter id="caballo-glow">
            <feGaussianBlur stdDeviation="3" result="coloredBlur"/>
            <feMerge>
              <feMergeNode in="coloredBlur"/>
              <feMergeNode in="SourceGraphic"/>
            </feMerge>
          </filter>
        </defs>
        <circle cx="50" cy="50" r="46" fill="url(#caballo-gradient)" opacity="0.2" />
        <circle cx="50" cy="50" r="42" fill="none" stroke="url(#caballo-gradient)" strokeWidth="1.5" opacity="0.4" />
        <g fill="none" stroke="url(#caballo-gradient)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" filter="url(#caballo-glow)">
          {/* Crin fluida y majestuosa */}
          <path d="M26 14 Q22 26 28 38" strokeWidth="2.5" />
          <path d="M32 10 Q30 22 34 34" strokeWidth="3" />
          <path d="M38 8 Q37 20 40 32" strokeWidth="3" />
          <path d="M44 8 Q44 18 46 28" strokeWidth="2.5" />
          <path d="M50 10 Q51 18 52 26" strokeWidth="2" />
          {/* Orejas puntiagudas */}
          <path d="M34 26 L28 14 L38 22" fill="url(#caballo-gradient)" opacity="0.3" />
          <path d="M58 26 L64 14 L54 22" fill="url(#caballo-gradient)" opacity="0.3" />
          <path d="M34 26 L28 14 L38 22" />
          <path d="M58 26 L64 14 L54 22" />
          {/* Cabeza noble */}
          <ellipse cx="46" cy="46" rx="24" ry="22" fill="url(#caballo-gradient)" opacity="0.15" />
          <ellipse cx="46" cy="46" rx="24" ry="22" />
          {/* Hocico alargado */}
          <ellipse cx="46" cy="72" rx="14" ry="12" fill="url(#caballo-gradient)" opacity="0.1" />
          <ellipse cx="46" cy="72" rx="14" ry="12" />
          {/* Conexión cabeza-hocico */}
          <path d="M32 62 L32 68" />
          <path d="M60 62 L60 68" />
          {/* Ojos expresivos */}
          <ellipse cx="38" cy="42" rx="5" ry="6" fill="url(#caballo-gradient)" />
          <ellipse cx="54" cy="42" rx="5" ry="6" fill="url(#caballo-gradient)" />
          <circle cx="40" cy="40" r="2" fill="white" opacity="0.9" />
          <circle cx="56" cy="40" r="2" fill="white" opacity="0.9" />
          {/* Fosas nasales */}
          <ellipse cx="40" cy="74" rx="4" ry="3" fill="url(#caballo-gradient)" />
          <ellipse cx="52" cy="74" rx="4" ry="3" fill="url(#caballo-gradient)" />
          {/* Detalle de boca */}
          <path d="M40 82 Q46 86 52 82" strokeWidth="1.5" />
        </g>
      </svg>
    ),
    cabra: (
      <svg viewBox="0 0 100 100" className={cn("", className)} width={iconSize} height={iconSize}>
        <defs>
          <linearGradient id="cabra-gradient" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#C4B5FD" />
            <stop offset="50%" stopColor="#A78BFA" />
            <stop offset="100%" stopColor="#8B5CF6" />
          </linearGradient>
          <filter id="cabra-glow">
            <feGaussianBlur stdDeviation="2" result="coloredBlur"/>
            <feMerge>
              <feMergeNode in="coloredBlur"/>
              <feMergeNode in="SourceGraphic"/>
            </feMerge>
          </filter>
        </defs>
        <circle cx="50" cy="50" r="46" fill="url(#cabra-gradient)" opacity="0.15" />
        <circle cx="50" cy="50" r="42" fill="none" stroke="url(#cabra-gradient)" strokeWidth="1" opacity="0.3" />
        <g fill="none" stroke="url(#cabra-gradient)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" filter="url(#cabra-glow)">
          {/* Cuernos curvados elegantes */}
          <path d="M28 38 Q16 28 12 38 Q8 50 18 48" strokeWidth="3" fill="url(#cabra-gradient)" opacity="0.25" />
          <path d="M72 38 Q84 28 88 38 Q92 50 82 48" strokeWidth="3" fill="url(#cabra-gradient)" opacity="0.25" />
          <path d="M28 38 Q16 28 12 38 Q8 50 18 48" strokeWidth="2.5" />
          <path d="M72 38 Q84 28 88 38 Q92 50 82 48" strokeWidth="2.5" />
          {/* Orejas caídas */}
          <path d="M24 48 L12 54 L24 58" fill="url(#cabra-gradient)" opacity="0.2" />
          <path d="M76 48 L88 54 L76 58" fill="url(#cabra-gradient)" opacity="0.2" />
          <path d="M24 48 L12 54 L24 58" />
          <path d="M76 48 L88 54 L76 58" />
          {/* Cabeza */}
          <ellipse cx="50" cy="56" rx="28" ry="26" fill="url(#cabra-gradient)" opacity="0.1" />
          <ellipse cx="50" cy="56" rx="28" ry="26" />
          {/* Ojos horizontales */}
          <ellipse cx="40" cy="50" rx="6" ry="5" fill="url(#cabra-gradient)" />
          <ellipse cx="60" cy="50" rx="6" ry="5" fill="url(#cabra-gradient)" />
          <ellipse cx="40" cy="50" rx="3" ry="4" fill="#000" opacity="0.4" />
          <ellipse cx="60" cy="50" rx="3" ry="4" fill="#000" opacity="0.4" />
          <circle cx="41" cy="48" r="1.5" fill="white" opacity="0.9" />
          <circle cx="61" cy="48" r="1.5" fill="white" opacity="0.9" />
          {/* Hocico */}
          <ellipse cx="50" cy="68" rx="10" ry="7" fill="url(#cabra-gradient)" opacity="0.15" />
          <ellipse cx="50" cy="68" rx="10" ry="7" />
          <circle cx="46" cy="68" r="2" fill="url(#cabra-gradient)" />
          <circle cx="54" cy="68" r="2" fill="url(#cabra-gradient)" />
          {/* Barba elegante */}
          <path d="M50 78 Q44 88 38 94" strokeWidth="2" />
          <path d="M50 78 Q50 90 50 96" strokeWidth="2.5" />
          <path d="M50 78 Q56 88 62 94" strokeWidth="2" />
        </g>
      </svg>
    ),
    mono: (
      <svg viewBox="0 0 100 100" className={cn("", className)} width={iconSize} height={iconSize}>
        <defs>
          <linearGradient id="mono-gradient" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#FDBA74" />
            <stop offset="50%" stopColor="#F97316" />
            <stop offset="100%" stopColor="#EA580C" />
          </linearGradient>
          <filter id="mono-glow">
            <feGaussianBlur stdDeviation="2" result="coloredBlur"/>
            <feMerge>
              <feMergeNode in="coloredBlur"/>
              <feMergeNode in="SourceGraphic"/>
            </feMerge>
          </filter>
        </defs>
        <circle cx="50" cy="50" r="46" fill="url(#mono-gradient)" opacity="0.15" />
        <circle cx="50" cy="50" r="42" fill="none" stroke="url(#mono-gradient)" strokeWidth="1" opacity="0.3" />
        <g fill="none" stroke="url(#mono-gradient)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" filter="url(#mono-glow)">
          {/* Orejas grandes */}
          <circle cx="18" cy="46" r="12" fill="url(#mono-gradient)" opacity="0.2" />
          <circle cx="82" cy="46" r="12" fill="url(#mono-gradient)" opacity="0.2" />
          <circle cx="18" cy="46" r="12" />
          <circle cx="82" cy="46" r="12" />
          <circle cx="18" cy="46" r="6" fill="url(#mono-gradient)" opacity="0.4" />
          <circle cx="82" cy="46" r="6" fill="url(#mono-gradient)" opacity="0.4" />
          {/* Cabeza */}
          <ellipse cx="50" cy="50" rx="30" ry="32" fill="url(#mono-gradient)" opacity="0.1" />
          <ellipse cx="50" cy="50" rx="30" ry="32" />
          {/* Área facial clara */}
          <ellipse cx="50" cy="56" rx="20" ry="18" fill="url(#mono-gradient)" opacity="0.2" />
          <ellipse cx="50" cy="56" rx="20" ry="18" strokeWidth="1.5" />
          {/* Ojos pícaros */}
          <circle cx="40" cy="46" r="6" fill="url(#mono-gradient)" />
          <circle cx="60" cy="46" r="6" fill="url(#mono-gradient)" />
          <circle cx="42" cy="44" r="2.5" fill="white" opacity="0.9" />
          <circle cx="62" cy="44" r="2.5" fill="white" opacity="0.9" />
          {/* Cejas expresivas */}
          <path d="M34 38 Q40 34 46 38" strokeWidth="2" />
          <path d="M54 38 Q60 34 66 38" strokeWidth="2" />
          {/* Nariz */}
          <ellipse cx="50" cy="58" rx="6" ry="5" fill="url(#mono-gradient)" />
          <circle cx="47" cy="58" r="1.5" fill="#000" opacity="0.3" />
          <circle cx="53" cy="58" r="1.5" fill="#000" opacity="0.3" />
          {/* Sonrisa traviesa */}
          <path d="M40 70 Q50 78 60 70" strokeWidth="2" />
          {/* Pelo en la cabeza */}
          <path d="M44 20 Q50 12 56 20" strokeWidth="2" />
          <path d="M48 18 Q50 10 52 18" strokeWidth="2.5" />
        </g>
      </svg>
    ),
    gallo: (
      <svg viewBox="0 0 100 100" className={cn("", className)} width={iconSize} height={iconSize}>
        <defs>
          <linearGradient id="gallo-gradient" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#FCA5A5" />
            <stop offset="50%" stopColor="#EF4444" />
            <stop offset="100%" stopColor="#B91C1C" />
          </linearGradient>
          <linearGradient id="gallo-comb" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor="#FCD34D" />
            <stop offset="100%" stopColor="#F59E0B" />
          </linearGradient>
          <filter id="gallo-glow">
            <feGaussianBlur stdDeviation="2" result="coloredBlur"/>
            <feMerge>
              <feMergeNode in="coloredBlur"/>
              <feMergeNode in="SourceGraphic"/>
            </feMerge>
          </filter>
        </defs>
        <circle cx="50" cy="50" r="46" fill="url(#gallo-gradient)" opacity="0.15" />
        <circle cx="50" cy="50" r="42" fill="none" stroke="url(#gallo-gradient)" strokeWidth="1" opacity="0.3" />
        <g fill="none" stroke="url(#gallo-gradient)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" filter="url(#gallo-glow)">
          {/* Cresta majestuosa */}
          <path d="M32 22 Q38 8 44 22 Q50 8 56 22 Q62 8 68 22" fill="url(#gallo-comb)" stroke="url(#gallo-comb)" strokeWidth="2" />
          <path d="M32 22 Q38 8 44 22 Q50 8 56 22 Q62 8 68 22" strokeWidth="1" />
          {/* Cabeza */}
          <ellipse cx="50" cy="48" rx="28" ry="24" fill="url(#gallo-gradient)" opacity="0.1" />
          <ellipse cx="50" cy="48" rx="28" ry="24" />
          {/* Plumas laterales */}
          <path d="M22 42 Q10 36 16 48" strokeWidth="2" />
          <path d="M22 50 Q8 50 14 56" strokeWidth="2" />
          <path d="M78 42 Q90 36 84 48" strokeWidth="2" />
          <path d="M78 50 Q92 50 86 56" strokeWidth="2" />
          {/* Ojo brillante */}
          <circle cx="42" cy="44" r="6" fill="url(#gallo-gradient)" />
          <circle cx="44" cy="42" r="2.5" fill="white" opacity="0.9" />
          <circle cx="42" cy="46" r="1.5" fill="white" opacity="0.5" />
          {/* Pico */}
          <path d="M68 48 L88 52 L68 58" fill="url(#gallo-comb)" stroke="url(#gallo-comb)" />
          <path d="M68 48 L88 52 L68 58" strokeWidth="1.5" />
          {/* Barbilla */}
          <path d="M42 68 Q36 82 46 78 Q52 82 48 68" fill="url(#gallo-gradient)" opacity="0.6" />
          <path d="M42 68 Q36 82 46 78 Q52 82 48 68" strokeWidth="1.5" />
        </g>
      </svg>
    ),
    perro: (
      <svg viewBox="0 0 100 100" className={cn("", className)} width={iconSize} height={iconSize}>
        <defs>
          <linearGradient id="perro-gradient" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#A5B4FC" />
            <stop offset="50%" stopColor="#6366F1" />
            <stop offset="100%" stopColor="#4F46E5" />
          </linearGradient>
          <filter id="perro-glow">
            <feGaussianBlur stdDeviation="2" result="coloredBlur"/>
            <feMerge>
              <feMergeNode in="coloredBlur"/>
              <feMergeNode in="SourceGraphic"/>
            </feMerge>
          </filter>
        </defs>
        <circle cx="50" cy="50" r="46" fill="url(#perro-gradient)" opacity="0.15" />
        <circle cx="50" cy="50" r="42" fill="none" stroke="url(#perro-gradient)" strokeWidth="1" opacity="0.3" />
        <g fill="none" stroke="url(#perro-gradient)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" filter="url(#perro-glow)">
          {/* Orejas caídas */}
          <path d="M18 38 Q12 16 28 24 L34 42" fill="url(#perro-gradient)" opacity="0.3" />
          <path d="M82 38 Q88 16 72 24 L66 42" fill="url(#perro-gradient)" opacity="0.3" />
          <path d="M18 38 Q12 16 28 24 L34 42" strokeWidth="2" />
          <path d="M82 38 Q88 16 72 24 L66 42" strokeWidth="2" />
          {/* Cabeza */}
          <ellipse cx="50" cy="52" rx="32" ry="30" fill="url(#perro-gradient)" opacity="0.1" />
          <ellipse cx="50" cy="52" rx="32" ry="30" />
          {/* Mancha facial */}
          <ellipse cx="50" cy="58" rx="16" ry="14" fill="url(#perro-gradient)" opacity="0.15" />
          {/* Ojos fieles */}
          <circle cx="38" cy="48" r="6" fill="url(#perro-gradient)" />
          <circle cx="62" cy="48" r="6" fill="url(#perro-gradient)" />
          <circle cx="40" cy="46" r="2.5" fill="white" opacity="0.9" />
          <circle cx="64" cy="46" r="2.5" fill="white" opacity="0.9" />
          {/* Cejas amigables */}
          <path d="M30 40 Q38 36 44 40" strokeWidth="2" />
          <path d="M56 40 Q62 36 70 40" strokeWidth="2" />
          {/* Nariz */}
          <ellipse cx="50" cy="62" rx="8" ry="6" fill="url(#perro-gradient)" />
          <ellipse cx="50" cy="60" rx="3" ry="2" fill="white" opacity="0.3" />
          {/* Boca y lengua */}
          <path d="M50 68 L50 74" strokeWidth="1.5" />
          <path d="M50 74 Q40 80 36 74" strokeWidth="1.5" />
          <path d="M50 74 Q60 80 64 74" strokeWidth="1.5" />
          <path d="M48 74 Q50 82 52 74" fill="url(#perro-gradient)" opacity="0.5" strokeWidth="1" />
        </g>
      </svg>
    ),
    cerdo: (
      <svg viewBox="0 0 100 100" className={cn("", className)} width={iconSize} height={iconSize}>
        <defs>
          <linearGradient id="cerdo-gradient" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#FBCFE8" />
            <stop offset="50%" stopColor="#F472B6" />
            <stop offset="100%" stopColor="#EC4899" />
          </linearGradient>
          <filter id="cerdo-glow">
            <feGaussianBlur stdDeviation="2" result="coloredBlur"/>
            <feMerge>
              <feMergeNode in="coloredBlur"/>
              <feMergeNode in="SourceGraphic"/>
            </feMerge>
          </filter>
        </defs>
        <circle cx="50" cy="50" r="46" fill="url(#cerdo-gradient)" opacity="0.15" />
        <circle cx="50" cy="50" r="42" fill="none" stroke="url(#cerdo-gradient)" strokeWidth="1" opacity="0.3" />
        <g fill="none" stroke="url(#cerdo-gradient)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" filter="url(#cerdo-glow)">
          {/* Orejas puntiagudas */}
          <path d="M20 32 Q12 20 24 16 Q36 14 32 32" fill="url(#cerdo-gradient)" opacity="0.3" />
          <path d="M80 32 Q88 20 76 16 Q64 14 68 32" fill="url(#cerdo-gradient)" opacity="0.3" />
          <path d="M20 32 Q12 20 24 16 Q36 14 32 32" />
          <path d="M80 32 Q88 20 76 16 Q64 14 68 32" />
          {/* Cabeza redonda */}
          <ellipse cx="50" cy="52" rx="34" ry="30" fill="url(#cerdo-gradient)" opacity="0.15" />
          <ellipse cx="50" cy="52" rx="34" ry="30" />
          {/* Mejillas rosadas */}
          <ellipse cx="28" cy="54" rx="6" ry="5" fill="url(#cerdo-gradient)" opacity="0.3" />
          <ellipse cx="72" cy="54" rx="6" ry="5" fill="url(#cerdo-gradient)" opacity="0.3" />
          {/* Ojos pequeños y brillantes */}
          <circle cx="38" cy="46" r="5" fill="url(#cerdo-gradient)" />
          <circle cx="62" cy="46" r="5" fill="url(#cerdo-gradient)" />
          <circle cx="40" cy="44" r="2" fill="white" opacity="0.9" />
          <circle cx="64" cy="44" r="2" fill="white" opacity="0.9" />
          {/* Hocico grande */}
          <ellipse cx="50" cy="62" rx="16" ry="12" fill="url(#cerdo-gradient)" opacity="0.25" />
          <ellipse cx="50" cy="62" rx="16" ry="12" />
          {/* Fosas nasales */}
          <ellipse cx="44" cy="62" rx="4" ry="5" fill="url(#cerdo-gradient)" />
          <ellipse cx="56" cy="62" rx="4" ry="5" fill="url(#cerdo-gradient)" />
          {/* Boca sonriente */}
          <path d="M42 74 Q50 80 58 74" strokeWidth="2" />
        </g>
      </svg>
    ),
  };

  return icons[animal.toLowerCase()] || icons.rata;
};

export default ChineseZodiacIcon;
